import React, {useEffect, useState} from 'react'
import {useAccessToken, useCustomerType} from '@salesforce/commerce-sdk-react'
import {Box} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useIntl} from 'react-intl'
import {contentStyle} from './index.style'
import useCustomFetch from '../../hooks/useFetchContent'
import {TrackOrderModal, useTrackOrderModal} from '../../hooks/use-track-order-modal'
import {AuthModal, useAuthModal} from '../../hooks/use-auth-modal'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'

const contentAssetIDs =
    'PWA-FOOTER-REGISTRATION,PWA-FOOTER-SUPPORT,PWA-FOOTER-ABOUTUS,PWA-FOOTER-CUSTOMERSERVICE,PWA-FOOTER-TERMS'

const findFooterLink = (footer, selector, label) => {
    const dataAttributeLink = footer.querySelector(selector)
    if (dataAttributeLink) {
        return dataAttributeLink
    }

    return Array.from(footer.querySelectorAll('a')).find(
        (link) => link.textContent.trim().toLowerCase() === label
    )
}

const Footer = () => {
    const {token} = useAccessToken()
    const {response: htmlString} = useCustomFetch(token, contentAssetIDs, false)
    const navigate = useNavigation()
    const {buildUrl} = useMultiSite()
    const {isRegistered} = useCustomerType()
    const trackOrderModal = useTrackOrderModal()
    const authModal = useAuthModal()
    const {formatMessage} = useIntl()
    const [footerEmail, setFooterEmail] = useState('')

    const handleTrackOrderClick = (e) => {
        e.stopPropagation()
        e.preventDefault()
        if (isRegistered) {
            navigate(`${buildUrl('/account/orders')}#ord-track`)
        } else {
            trackOrderModal.onOpen()
        }
    }

    const handleRegistrationFormSubmit = (event) => {
        event.preventDefault()
        const form = event.target
        form.classList.remove('footer__registration-form--error')
        if (!form.checkValidity()) {
            form.classList.add('footer__registration-form--error')
            return
        }

        const email = new FormData(form).get('email')
        setFooterEmail(String(email || '').trim())
        authModal.onOpen()
    }

    const handleAccountButtonClick = (event) => {
        event.preventDefault()
        navigate(buildUrl('/account'))
    }

    useEffect(() => {
        if (!htmlString) {
            return
        }

        const footer = document.querySelector('.footer__container')
        if (!footer) {
            return
        }

        const orderTrackPath = `${buildUrl('/account/orders')}#ord-track`
        const accessibilityPath = buildUrl('/content/digital-accessibility.html')

        const orderTrackEl = findFooterLink(footer, '[data-js-order-tracking]', 'order tracking')
        const accessibilityEl = findFooterLink(
            footer,
            '[data-js-accessibility-policy]',
            'accessibility policy'
        )

        if (orderTrackEl) {
            orderTrackEl.href = orderTrackPath
            orderTrackEl.addEventListener('click', handleTrackOrderClick)
        }

        if (accessibilityEl) {
            accessibilityEl.href = accessibilityPath
        }

        const form = footer.querySelector('[data-js-footer-registration-form]')
        if (form) {
            form.addEventListener('submit', handleRegistrationFormSubmit)
        }

        const registration = footer.querySelector('.footer__registration')
        const loyaltyDescription = footer.querySelector('.footer__registration-loyalty-description')
        let accountButton = footer.querySelector('[data-js-footer-account-button]')

        if (loyaltyDescription) {
            if (!loyaltyDescription.dataset.guestLoyaltyDescription) {
                loyaltyDescription.dataset.guestLoyaltyDescription = loyaltyDescription.textContent
            }
            loyaltyDescription.textContent = isRegistered
                ? formatMessage({
                      id: 'footer.loyalty_description.registered',
                      defaultMessage:
                          'Members earn 1 point for every dollar spent on purchases. For every 200 points members receive a $5 reward.'
                  })
                : loyaltyDescription.dataset.guestLoyaltyDescription
        }

        if (isRegistered && registration) {
            if (!accountButton) {
                accountButton = document.createElement('button')
                accountButton.type = 'button'
                accountButton.className = 'footer__registration-button'
                accountButton.setAttribute('data-js-footer-account-button', '')
                accountButton.textContent = `${formatMessage({
                    id: 'header.link.my_account',
                    defaultMessage: 'My Account'
                })} >>`

                if (form) {
                    form.insertAdjacentElement('afterend', accountButton)
                } else {
                    registration.appendChild(accountButton)
                }
            }
            accountButton.addEventListener('click', handleAccountButtonClick)
        } else if (accountButton) {
            accountButton.remove()
        }

        return () => {
            if (form) {
                form.removeEventListener('submit', handleRegistrationFormSubmit)
            }
            if (orderTrackEl) {
                orderTrackEl.removeEventListener('click', handleTrackOrderClick)
            }
            const button = footer.querySelector('[data-js-footer-account-button]')
            if (button) {
                button.removeEventListener('click', handleAccountButtonClick)
            }
        }
    }, [htmlString, isRegistered, formatMessage])

    useEffect(() => {
        window.semaphore = window.semaphore || []
        window.ketch = function () {
            window.semaphore.push(arguments)
        }

        const script = document.createElement('script')
        script.type = 'text/javascript'
        script.src = 'https://global.ketchcdn.com/web/v3/config/cavenders/website_smart_tag/boot.js'
        script.defer = true
        script.async = true
        document.getElementsByTagName('head')[0].appendChild(script)

        const handleKetchClick = (event) => {
            const anchor = event.target.closest('a[onclick*="showPreferences"]')
            if (!anchor) return
            event.preventDefault()
            window.semaphore.push(['showPreferences'])
        }
        document.body.addEventListener('click', handleKetchClick)

        return () => {
            document.body.removeEventListener('click', handleKetchClick)
            document.getElementsByTagName('head')[0].removeChild(script)
        }
    }, [])

    return (
        <Box
            as="footer"
            className={isRegistered ? 'footer footer--registered' : 'footer'}
            sx={contentStyle}
        >
            <Box className="footer__container" dangerouslySetInnerHTML={{__html: htmlString}}></Box>
            <TrackOrderModal {...trackOrderModal} />
            <AuthModal {...authModal} initialEmail={footerEmail} />
        </Box>
    )
}

export default Footer
